import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { CalendarDays, Loader2, Users } from "lucide-react";
import { API } from "@/lib/api";
import { ItineraryLine } from "@/components/Itinerary";

export default function SharedItinerary() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/itineraries/${id}`)
      .then((res) => setData(res.data))
      .catch(() => setNotFound(true));
  }, [id]);

  return (
    <div data-testid="shared-itinerary-page" className="min-h-screen bg-[#070709] text-neutral-100">
      <div className="mx-auto max-w-3xl px-5 py-14 sm:py-20">
        <div className="flex flex-col items-center text-center">
          <img src="/assets/logo.png" alt="Exclusive Reykjavik" className="h-16 w-auto mix-blend-screen" />
          <p className="mt-8 text-[10px] font-semibold uppercase tracking-[0.35em] text-amber-300/80">
            A private journey, composed for you
          </p>
        </div>

        {notFound ? (
          <div data-testid="shared-itinerary-not-found" className="mt-14 border border-dashed border-neutral-700 py-20 text-center">
            <p className="font-serif text-3xl font-light text-neutral-300">This itinerary has wandered off the map.</p>
            <p className="mt-3 text-sm text-neutral-500">The link may be incomplete — ask your concierge to resend it.</p>
          </div>
        ) : !data ? (
          <div className="mt-20 flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-amber-400" />
          </div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <div data-testid="shared-itinerary-meta" className="mt-8 flex flex-wrap items-center justify-center gap-3 text-[11px] uppercase tracking-[0.2em] text-neutral-400">
              {(data.arrival_date || data.departure_date) && (
                <span className="flex items-center gap-2 border border-amber-500/25 px-3 py-1.5">
                  <CalendarDays className="h-3.5 w-3.5 text-amber-400/70" />
                  {data.arrival_date || "?"} → {data.departure_date || "?"}
                </span>
              )}
              {data.party_size && (
                <span className="flex items-center gap-2 border border-amber-500/25 px-3 py-1.5">
                  <Users className="h-3.5 w-3.5 text-amber-400/70" /> Party of {data.party_size}
                </span>
              )}
            </div>

            {data.services?.length > 0 && (
              <div className="mt-5 flex flex-wrap justify-center gap-2">
                {data.services.map((s) => (
                  <span key={s} className="border border-amber-500/20 bg-amber-500/5 px-2.5 py-1 text-xs text-amber-100/80">
                    {s}
                  </span>
                ))}
              </div>
            )}

            <div data-testid="shared-itinerary-body" className="mt-10 border border-amber-500/20 bg-[#0c0c10]/80 p-6 sm:p-10">
              {data.text.split("\n").map((line, i) => (
                <ItineraryLine key={i} line={line} />
              ))}
              <p data-testid="itinerary-disclaimer" className="mt-8 border-t border-neutral-800/60 pt-4 text-[9px] leading-relaxed tracking-[0.08em] text-neutral-600">
                There can be mistakes in pricing. This never happens, but if it does, we do not take responsibility if pricing is wrong.
                There is a 7 day cancellation policy on everything after it is confirmed with 0% refunded unless we can get money back from the tour operator.
              </p>
            </div>

            <p className="mt-8 text-center font-serif text-sm italic text-amber-200/60">
              Every detail on this page can be arranged exactly as written — or entirely reimagined.
            </p>
          </motion.div>
        )}

        <div className="mt-12 text-center">
          <Link
            to="/"
            data-testid="shared-itinerary-cta"
            className="inline-block bg-amber-500 px-8 py-3.5 text-xs font-semibold uppercase tracking-[0.25em] text-black transition-colors hover:bg-amber-300"
          >
            Compose Your Own Journey
          </Link>
          <p className="mt-6 text-[10px] uppercase tracking-[0.3em] text-neutral-600">
            Exclusive Reykjavik · Key to the city — and beyond
          </p>
        </div>
      </div>
    </div>
  );
}
